import { ApiService } from '@app/services/api/api.service';
import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { LayoutService } from 'src/app/layout/service/app.layout.service';
import { RoutePath } from '@app/app.routes';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { StoreService } from '@app/services/store/store.service';
import { GUI_VISIBILITY_MATCH, PERMISSION_TYPE_DENY, plans } from '@app/modules/client/constant';
import { accessRole, isPlatformAdmin } from '@app/models/access-role';

export function isCompactSidebar(
  viewportWidth: number,
  sidebarWidth: number,
  availableHeight = Infinity,
  expandedHeight = 0
): boolean {
  return (
    viewportWidth <= 991 ||
    (sidebarWidth > 0 && sidebarWidth < 200) ||
    (expandedHeight > 0 && availableHeight < expandedHeight)
  );
}

@Component({
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
})
export class AppMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly workspaceNavigation = true;
  workspaceArea: 'home' | 'settings' | 'admin' = 'home';
  compactNavigation = window.innerWidth <= 991;
  navigationScale = 1;
  workspaceMenu: any[] = [];
  private navigationObserver?: ResizeObserver;
  private expandedNavigationHeight = 0;
  get isPlatformAdmin(): boolean {
    return isPlatformAdmin(this.store.getUser());
  }
  get isAgent(): boolean {
    return accessRole(this.store.getUser()) === 'Agent';
  }
  private readonly adminItems = [
    'Database Backups',
    'Cluster Management',
    'System Settings',
    'Call Trace',
    'System Dialogs',
    'System Audits',
    'Email Signups',
  ];
  private readonly settingItems = [
    'Manage Roles',
    'Manage Billings',
    'Manage Products',
    'Whitelist',
    'SIP Gateways',
    'SIP Connections',
    'Client Setup (Preview)',
  ];
  private readonly customerPaths: Record<string, string> = {
    'Call Logs': 'call-log',
    'Tracking Numbers': 'tracking-number',
    'Tracking Sources': 'tracking-source',
    'Receiving Numbers': 'routing/receiving',
    Extensions: 'routing/extensions',
    'SIP Gateways': 'routing/sipgateway',
    'Voice Menus': 'ivr',
  };
  selectWorkspaceArea(area: 'home' | 'settings' | 'admin'): void {
    if (area === 'admin' && !this.isPlatformAdmin) return;
    this.workspaceArea = area;
    this.rebuildWorkspaceMenu();
  }
  ngAfterViewInit(): void {
    if (!this.workspaceNavigation) return;
    this.updateNavigationMode();
    this.updateNavigationScale();
    if (typeof ResizeObserver !== 'undefined') {
      this.navigationObserver = new ResizeObserver(() =>
        this.zone.run(() => {
          this.updateNavigationMode();
          this.updateNavigationScale();
        })
      );
      const sidebar = (this.el.nativeElement as HTMLElement).closest('.layout-sidebar');
      if (sidebar) this.navigationObserver.observe(sidebar);
      const container = sidebar?.querySelector('.layout-menu-container');
      if (container) this.navigationObserver.observe(container);
      const navigation = (this.el.nativeElement as HTMLElement).querySelector('.ecms-navigation');
      if (navigation) this.navigationObserver.observe(navigation);
      const slot = (this.el.nativeElement as HTMLElement).querySelector('.ecms-navigation-main-slot');
      if (slot) this.navigationObserver.observe(slot);
      const main = (this.el.nativeElement as HTMLElement).querySelector('.ecms-navigation-main');
      if (main) this.navigationObserver.observe(main);
      const footer = (this.el.nativeElement as HTMLElement).querySelector('.ecms-navigation-footer');
      if (footer) this.navigationObserver.observe(footer);
    }
  }
  ngOnDestroy(): void {
    this.navigationObserver?.disconnect();
  }
  @HostListener('window:resize') onWindowResize(): void {
    this.updateNavigationMode();
    this.updateNavigationScale();
  }
  private availableNavigationHeight(container: HTMLElement | null, navigation: HTMLElement | null): number {
    const slot = navigation?.querySelector('.ecms-navigation-main-slot') as HTMLElement | null;
    if (slot) return slot.clientHeight;
    const element = navigation?.clientHeight ? navigation : container;
    if (!element) return 0;
    const style = getComputedStyle(element);
    const footer = navigation?.querySelector('.ecms-navigation-footer') as HTMLElement | null;
    return (
      element.clientHeight -
      (parseFloat(style.paddingTop) || 0) -
      (parseFloat(style.paddingBottom) || 0) -
      (element === navigation ? (footer?.offsetHeight ?? 0) : 0)
    );
  }
  private updateNavigationMode(): void {
    if (!this.workspaceNavigation) return;
    const sidebar = (this.el.nativeElement as HTMLElement).closest('.layout-sidebar');
    const container = sidebar?.querySelector('.layout-menu-container') as HTMLElement | null;
    const navigation = (this.el.nativeElement as HTMLElement).querySelector('.ecms-navigation') as HTMLElement | null;
    const main = navigation?.querySelector('.ecms-navigation-main') as HTMLElement | null;
    if (!this.compactNavigation && this.workspaceArea !== 'admin' && this.isMenuLoaded && main) {
      this.expandedNavigationHeight = main.scrollHeight;
    }
    const compact = isCompactSidebar(
      window.innerWidth,
      sidebar?.getBoundingClientRect().width ?? 0,
      this.availableNavigationHeight(container, navigation),
      this.expandedNavigationHeight
    );
    if (compact !== this.compactNavigation) {
      this.compactNavigation = compact;
      this.rebuildWorkspaceMenu();
    }
  }
  private updateNavigationScale(): void {
    if (!this.workspaceNavigation) return;
    const sidebar = (this.el.nativeElement as HTMLElement).closest('.layout-sidebar');
    const container = sidebar?.querySelector('.layout-menu-container') as HTMLElement | null;
    const navigation = (this.el.nativeElement as HTMLElement).querySelector('.ecms-navigation') as HTMLElement | null;
    const main = navigation?.querySelector('.ecms-navigation-main') as HTMLElement | null;
    const availableHeight = this.availableNavigationHeight(container, navigation);
    if (availableHeight > 0 && main?.scrollHeight) {
      this.navigationScale = Math.min(1, availableHeight / main.scrollHeight);
    }
  }
  private rebuildWorkspaceMenu(): void {
    if (!this.workspaceNavigation && accessRole(this.store.getUser()) !== 'Agent') return;
    const customerScope = this.isPlatformAdmin && this.url.startsWith('/service/customer-panel');
    const items = this.menu
      .reduce((all, group) => all.concat(group.items), [] as any[])
      .filter(item => item.visible && item.label !== 'Customer Panel');
    const visibleItems = items
      .filter(item => {
        if (this.isPlatformAdmin && item.label === 'Extensions') return false;
        if (customerScope && !this.customerPaths[item.label]) return false;
        return true;
      })
      .map(item => ({
        ...item,
        routerLink:
          customerScope && this.workspaceArea !== 'admin'
            ? ['/service/customer-panel/' + this.customerPaths[item.label]]
            : item.routerLink,
      }));
    if (this.workspaceArea === 'admin') {
      const filtered = items.filter(item => this.isPlatformAdmin && this.adminItems.includes(item.label));
      const groups = [
        ['Diagnostics', ['Call Trace']],
        ['Backups & Recovery', ['Database Backups']],
        ['Servers & Configuration', ['Cluster Management', 'System Settings', 'Email Signups']],
      ];
      this.workspaceMenu = [
        {
          label: 'Administration',
          visible: true,
          items: [
            {
              label: 'Administrative Dashboard',
              icon: 'pi pi-fw pi-home',
              routerLink: ['/service/dashboard/admin'],
              visible: true,
            },
          ],
        },
        ...groups.map(([label, names]) => ({
          label,
          visible: true,
          items: filtered.filter(item => names.includes(item.label)),
        })),
      ];
    } else {
      const customerSettings = ['Receiving Numbers', 'Extensions', 'SIP Gateways', 'Voice Menus'];
      const available = visibleItems.filter(item => !(this.isPlatformAdmin && this.adminItems.includes(item.label)));
      const settings = (item: any) =>
        customerScope ? customerSettings.includes(item.label) : this.settingItems.includes(item.label);
      const display = (item: any) => ({
        ...item,
        label: item.label === 'Voice Menus' ? 'IVR / Voice Menus' : item.label,
      });
      const homeItems = available.filter(item => !settings(item)).map(display);
      const settingItems = available.filter(settings).map(display);
      this.workspaceMenu = this.compactNavigation
        ? [
            {
              label: this.workspaceArea === 'settings' ? 'Settings' : 'Home',
              visible: true,
              items: this.workspaceArea === 'settings' ? settingItems : homeItems,
            },
          ]
        : [
            { label: 'Home', visible: true, items: homeItems },
            { label: 'Settings', visible: true, items: settingItems },
          ];
    }
  }
  menu: any[] = [];
  isMenuLoaded = false;
  backupsEnabled = false;
  url: string;
  user: any = {};

  constructor(
    public layoutService: LayoutService,
    private router: Router,
    private store: StoreService,
    private api: ApiService,
    public el: ElementRef,
    private zone: NgZone
  ) {
    this.url = this.router.url;
    if (this.url.startsWith('/service/dashboard/admin')) this.workspaceArea = 'admin';
    router.events.subscribe(event => {
      if (this.workspaceNavigation ? event instanceof NavigationEnd : event instanceof NavigationStart) {
        // Navigation started.
        this.url = event instanceof NavigationEnd ? event.urlAfterRedirects : (event as NavigationStart).url;
        if (this.url.startsWith('/service/dashboard/admin')) this.workspaceArea = 'admin';
        else if (this.url === '/service/dashboard') this.workspaceArea = 'home';
        this.rebuildWorkspaceMenu();
      }
    });
  }

  async ngOnInit() {
    this.menu = [
      {
        label: 'Activities',
        icon: 'pi pi-home',
        visible: true,
        items: [
          { label: 'Dashboard', visible: true, icon: 'pi pi-fw pi-home', routerLink: [RoutePath.dashboard] },
          { label: 'Call Logs', visible: true, icon: 'pi pi-fw pi-phone', routerLink: [RoutePath.callLogs] },
        ],
      },
      {
        label: 'Number Management',
        visible: true,
        icon: 'pi pi-fw pi-shopping-cart',
        items: [
          {
            label: 'Buy Numbers',
            visible: true,
            icon: 'pi pi-fw pi-shopping-cart',
            routerLink: [RoutePath.numberman.management],
          },
          {
            label: 'Tracking Numbers',
            visible: true,
            icon: 'pi pi-fw pi-sync',
            routerLink: [RoutePath.tracking_number.numbers],
          },
          {
            label: 'Receiving Numbers',
            visible: true,
            icon: 'pi pi-fw pi-sliders-h',
            routerLink: [RoutePath.receiving.numbers],
          },
          {
            label: 'Extensions',
            visible: true,
            icon: 'pi pi-fw pi-phone',
            routerLink: [RoutePath.receiving.extensions],
          },
        ],
      },
      {
        label: 'Call Source',
        icon: 'pi pi-fw pi-sync',
        visible: true,
        items: [
          {
            label: 'Tracking Sources',
            visible: true,
            icon: 'pi pi-fw pi-sync',
            routerLink: [RoutePath.tracking_source.sources],
          },
        ],
      },
      {
        label: 'Routing',
        visible: true,
        icon: 'pi pi-fw pi-sliders-v',
        items: [
          {
            label: 'Whitelist',
            visible: true,
            icon: 'pi pi-fw pi-verified',
            routerLink: [RoutePath.receiving.whitelist],
          },
          {
            label: 'SIP Gateways',
            visible: true,
            icon: 'pi pi-fw pi-wave-pulse',
            routerLink: [RoutePath.receiving.sipgateways],
          },
          { label: 'Voice Menus', visible: true, icon: 'pi pi-fw pi-sitemap', routerLink: [RoutePath.ivr] },
        ],
      },
      {
        label: 'Reporting',
        visible: true,
        icon: 'pi pi-fw pi-chart-bar',
        items: [
          {
            label: 'Activity Reports',
            visible: true,
            icon: 'pi pi-fw pi-chart-bar',
            routerLink: [RoutePath.reports.activity_reports],
          },
          {
            label: 'Overview Reports',
            visible: true,
            icon: 'pi pi-fw pi-desktop',
            routerLink: [RoutePath.reports.overview],
          },
          {
            label: 'System Dialogs',
            visible: true,
            icon: 'pi pi-fw pi-arrow-right-arrow-left',
            routerLink: [RoutePath.system.dialogs],
          },
          { label: 'System Audits', visible: true, icon: 'pi pi-fw pi-id-card', routerLink: [RoutePath.system.audit] },
        ],
      },
      {
        label: 'Settings',
        icon: 'pi pi-fw pi-users',
        visible: true,
        items: [
          {
            label: 'Customer Panel',
            visible: true,
            icon: 'pi pi-fw pi-briefcase',
            routerLink: [RoutePath.customer_panel.customer_panel],
          },
          {
            label: 'Account Profile',
            visible: true,
            icon: 'pi pi-fw pi-id-card',
            routerLink: [RoutePath.user.account],
          },
          {
            label: 'SIP Connections',
            visible: true,
            icon: 'pi pi-fw pi-link',
            routerLink: ['/service/customer/sip-connections'],
          },
          {
            label: 'Manage Customers',
            visible: true,
            icon: 'pi pi-fw pi-truck',
            routerLink: [RoutePath.customer.customers],
          },
          { label: 'Manage Roles', visible: true, icon: 'pi pi-fw pi-user-edit', routerLink: [RoutePath.role.roles] },
          {
            label: 'Manage Billings',
            visible: true,
            icon: 'pi pi-fw pi-dollar',
            routerLink: [RoutePath.billing.settings],
          },
          {
            label: 'Manage Products',
            visible: true,
            icon: 'pi pi-fw pi-credit-card',
            routerLink: [RoutePath.product.products],
          },
          {
            label: 'Database Backups',
            visible: false,
            icon: 'pi pi-fw pi-cloud-upload',
            routerLink: ['/service/settings/database-backups'],
          },
          {
            label: 'Cluster Management',
            visible: true,
            icon: 'pi pi-fw pi-sitemap',
            routerLink: ['/service/settings/cluster'],
          },
          {
            label: 'System Settings',
            visible: true,
            icon: 'pi pi-fw pi-cog',
            routerLink: [RoutePath.systemSetting.systemSettings],
          },
          { label: 'Call Trace', visible: false, icon: 'pi pi-fw pi-share-alt', routerLink: [RoutePath.callTrace] },
          {
            label: 'Email Signups',
            visible: true,
            icon: 'pi pi-fw pi-envelope',
            routerLink: [RoutePath.system.emailSignups],
          },
        ],
      },
    ];
    {
      this.menu.splice(1, 0, {
        label: 'Answering Service',
        visible: true,
        icon: 'pi pi-headphones',
        items: [
          {
            label: 'Agent Workspace (Preview)',
            visible: true,
            icon: 'pi pi-phone',
            routerLink: ['/service/answering-service/agent'],
          },
          {
            label: 'Client Setup (Preview)',
            visible: true,
            icon: 'pi pi-users',
            routerLink: ['/service/answering-service/clients'],
          },
        ],
      });
    }
    await new Promise<void>(resolve => {
      const mainUserInterval = setInterval(() => {
        if (this.store.getUser() && this.store.getGuiVisibility()) {
          clearInterval(mainUserInterval);

          resolve();
        }
      }, 100);
    });

    if (this.isPlatformAdmin) {
      this.api.getDatabaseBackupConfiguration().subscribe({
        next: configuration => {
          this.backupsEnabled = configuration.enabled;
          this.setMenuHidden(this.store.getGuiVisibility());
        },
        error: () => {
          this.backupsEnabled = false;
        },
      });
    }
    this.store.state$.subscribe(async state => {
      if (state.token && state.user && state.guiVisibility) {
        this.user = this.store.getUser();
        this.setMenuHidden(state.guiVisibility);
      }
    });

    this.layoutService.configUpdate$.subscribe(res => {
      this.store.state$.subscribe(async state => {
        if (state.token && state.user && state.guiVisibility) {
          this.user = this.store.getUser();
          this.setMenuHidden(state.guiVisibility);
        }
      });
    });
  }

  setMenuHidden = guiVisibility => {
    if (accessRole(this.store.getUser()) === 'Agent') {
      this.menu = [
        {
          label: 'Answering Service',
          visible: true,
          items: [
            {
              label: 'Agent Workspace',
              visible: true,
              icon: 'pi pi-phone',
              routerLink: ['/service/answering-service/agent'],
            },
          ],
        },
      ];
      this.isMenuLoaded = true;
      this.rebuildWorkspaceMenu();
      return;
    }
    const { isCustomerAdmin } = this.store.getUserPermissions();
    const isSuperAdmin = this.isPlatformAdmin;
    const superAdminRestrictedPages = ['Account Profile', 'Buy Numbers'];
    const hideSipGatewaysForPlan = !isSuperAdmin && this.getActivePlanSku() === plans.basic;

    for (const subMenuLvl1 of this.menu) {
      subMenuLvl1.visible = false;
      let bAllLvl2Hidden = true;

      for (const subMenuLvl2 of subMenuLvl1.items) {
        subMenuLvl2.visible = false;
        if (subMenuLvl2.label === 'Manage Customers' && !isSuperAdmin) continue;
        if (subMenuLvl2.label === 'SIP Connections' && (isSuperAdmin || !isCustomerAdmin)) continue;
        if (subMenuLvl2.label === 'Database Backups' && (!isSuperAdmin || !this.backupsEnabled)) continue;
        if (subMenuLvl2.label === 'SIP Gateways' && hideSipGatewaysForPlan) {
          continue;
        }
        if (['Call Trace', 'Cluster Management'].includes(subMenuLvl2.label) && !isSuperAdmin) {
          continue;
        }

        if (subMenuLvl2.label == 'Voice Menus' && (isSuperAdmin || isCustomerAdmin)) {
          subMenuLvl2.visible = true;
          bAllLvl2Hidden = false;
          continue;
        }
        if (
          subMenuLvl2.label === 'Extensions' &&
          !isSuperAdmin &&
          (isCustomerAdmin ||
            guiVisibility.some(
              v => v.GuiSection?.name === 'ReceivingNumbers' && v.GuiPermission?.name !== PERMISSION_TYPE_DENY
            ))
        ) {
          subMenuLvl2.visible = true;
          bAllLvl2Hidden = false;
          continue;
        }

        const customerExternalProviders = this.store.getUser()?.Customer?.NumberProviders || [];
        const isShowBuyNumber = !customerExternalProviders?.length || customerExternalProviders.some(x => x.enabled);
        if (subMenuLvl2.label === 'Buy Numbers' && !isShowBuyNumber) {
          subMenuLvl2.visible = false;
          bAllLvl2Hidden = false;
          continue;
        }

        if (isSuperAdmin) {
          subMenuLvl2.visible = true;
          bAllLvl2Hidden = false;

          if (superAdminRestrictedPages.includes(subMenuLvl2.label)) {
            subMenuLvl2.visible = false;
          }

          continue;
        } else {
          if (
            subMenuLvl2.label === 'System Settings' ||
            subMenuLvl2.label === 'System Audits' ||
            subMenuLvl2.label === 'Email Signups'
          ) {
            subMenuLvl2.visible = false;
            continue;
          } else if (
            (subMenuLvl2.label == 'Account Profile' ||
              subMenuLvl2.label == 'Account Management' ||
              subMenuLvl2.label == 'SIP Connections' ||
              subMenuLvl2.label == 'Manage Billings' ||
              subMenuLvl2.label == 'Manage Products') &&
            isCustomerAdmin
          ) {
            subMenuLvl2.visible = true;
            bAllLvl2Hidden = false;
            continue;
          }
        }

        for (const v of guiVisibility) {
          if (GUI_VISIBILITY_MATCH[v.GuiSection.name] == subMenuLvl2.label) {
            if (v.GuiPermission.name != PERMISSION_TYPE_DENY) {
              subMenuLvl2.visible = true;
              bAllLvl2Hidden = false;
            }
          }
        }
      }

      if (!bAllLvl2Hidden) subMenuLvl1.visible = true;
    }

    if (isCustomerAdmin && !isSuperAdmin) {
      for (const group of this.menu)
        for (const item of group.items || []) {
          if (item.label === 'Account Profile') {
            item.label = 'Account Management';
            item.routerLink = ['/service/customer/account'];
          }
        }
    }
    this.isMenuLoaded = true;
    this.rebuildWorkspaceMenu();
  };

  private getActivePlanSku(): string | null {
    const planProduct = this.store
      .getUser()
      ?.Customer?.ProductVariations?.find((product: any) => product?.Product?.group === 'plan');
    return planProduct?.Product?.sku || null;
  }
}
